import React, { useState } from 'react';
import { Package, Type, ChevronDown, HelpCircle, Eye, EyeOff } from 'lucide-react';
import { useTextSize } from '../contexts/TextSizeContext';
import { useTeachingMode } from '../contexts/TeachingModeContext';
import TeachingModeSelector from './TeachingModeSelector';

const Header = ({ companyInfo, currentPeriod, periodStatus }) => {
  const { textSize, setTextSize, textSizeConfig, isManualSelection, resetToAuto } = useTextSize();
  const { showTeachingAids, toggleTeachingAids, isAssistMode } = useTeachingMode();
  const [showTextSizeMenu, setShowTextSizeMenu] = useState(false);

  const handleTextSizeChange = (size) => {
    setTextSize(size);
    setShowTextSizeMenu(false);
  };

  const handleAutoMode = () => {
    resetToAuto();
    setShowTextSizeMenu(false);
  };

  // Determine button styling based on state and mode
  const getToggleButtonStyle = () => {
    if (showTeachingAids) {
      return 'bg-blue-100 text-blue-700 hover:bg-blue-200 ring-2 ring-blue-300';
    } else {
      return 'bg-gray-100 text-gray-600 hover:bg-gray-200';
    }
  };

  const getTooltipText = () => {
    if (showTeachingAids) {
      return 'Hide Teaching Assistant Panel';
    } else {
      return 'Show Teaching Assistant Panel';
    }
  };

  return (
    <div className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Package className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Enterprise Resource Planning System</h1>
              <p className="text-sm text-gray-600">Professional Business Workflow Management Platform</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Teaching Mode Selector */}
            <TeachingModeSelector />
            
            {/* Teaching Aids Toggle - Master Control for all modes */}
            <button
              onClick={toggleTeachingAids}
              className={`p-2 rounded-lg transition-all duration-200 ${getToggleButtonStyle()}`}
              title={getTooltipText()}
            >
              {showTeachingAids ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
            
            {/* Text Size Controls */}
            <div className="relative">
              <button
                onClick={() => setShowTextSizeMenu(!showTextSizeMenu)}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                title="調整文字大小"
              >
                <Type className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {textSizeConfig[textSize].labelCh}
                </span>
                <ChevronDown className="w-3 h-3" />
              </button>

              {showTextSizeMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-2">
                    <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                      文字大小設定
                    </div>
                    
                    {Object.entries(textSizeConfig).map(([size, config]) => (
                      <button
                        key={size}
                        onClick={() => handleTextSizeChange(size)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                          textSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                        }`}
                      >
                        <span>{config.label} ({config.labelCh})</span>
                        {textSize === size && (
                          <span className="text-blue-600">✓</span>
                        )}
                      </button>
                    ))}
                    
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleAutoMode}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          !isManualSelection() ? 'bg-green-50 text-green-600' : 'text-gray-700'
                        }`}
                      >
                        <span className="flex items-center justify-between">
                          <span>自動調整</span>
                          {!isManualSelection() && (
                            <span className="text-green-600">✓</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 block mt-1">
                          根據螢幕大小自動調整
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* System Status */}
            <div className="text-right text-sm text-gray-600">
              <p>會計期間: {currentPeriod}</p>
              <p className="flex items-center justify-end space-x-1">
                <span>狀態:</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  periodStatus === 'open' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {periodStatus === 'open' ? '開放' : '已關閉'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Click outside to close menu */}
      {showTextSizeMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowTextSizeMenu(false)}
        />
      )}
    </div>
  );
};

export default Header; 